import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { useAppRegistry } from '@/api/queries/useAppRegistry';
import {
  useTenantApps,
  useUnlockTenantApp,
  useLockTenantApp,
  useSetTenantAppAllowed
} from '@/pages/admin/api/queries/useTenantApps';
import { usePendingRowIds } from '@/api/mutations/usePendingRowIds';
import { appKeys } from '@/api/query-keys';
import { appConsoleStatus, mergeAppCatalog, type MergedAppRow } from '@/lib/merge-app-catalog';
import { Button, ConfirmDialog, Separator } from '@bliks-io/ui';
import {
  PROCESS_ANALYZER_APP_ID,
  COMPLIANCE_CHECK_APP_ID,
  SALES_APP_ID,
  type TenantSummary
} from '@bliks-io/contracts';
import {
  AppDetailSwitch,
  AppMasterDetail,
  AppStatusBadge
} from '@/components/app-console/AppMasterDetail';
import { AddTenantAppDialog } from './AddTenantAppDialog';
import { TenantAppSeatLimitCell } from './TenantAppSeatLimitCell';
import { TenantProcessAnalyzerConfigCell } from './TenantProcessAnalyzerConfigCell';
import { TenantComplianceCheckConfigCell } from './TenantComplianceCheckConfigCell';
import { TenantSalesScopeCell } from './TenantSalesScopeCell';

interface TenantAppsTabProps {
  tenantId: string;
  /**
   * The tenant itself, when the caller has it: the sales cell reads the
   * scope off it, and a partner tenant shows no seats (SAL-09).
   */
  tenant?: TenantSummary;
}

/** Whether removing this app would discard state a re-add cannot restore. */
function hasSeatsOrConfig(app: MergedAppRow): boolean {
  const hasSeats = (app.assignedUserIds?.length ?? 0) > 0;
  const hasConfig =
    app.config != null && (typeof app.config !== 'object' || Object.keys(app.config).length > 0);
  return hasSeats || hasConfig;
}

/**
 * The superadmin's app surface for one tenant: which apps the tenant has, what
 * it may use, how many seats it gets and how each app is configured. Activation
 * is deliberately absent — that belongs to the tenant's own system admin.
 */
export function TenantAppsTab({ tenantId, tenant }: TenantAppsTabProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { data: registryApps, isLoading: registryLoading } = useAppRegistry();
  const { data: installedApps, isLoading: appsLoading } = useTenantApps(tenantId);
  const addMutation = useUnlockTenantApp(tenantId);
  const removeMutation = useLockTenantApp(tenantId);
  const setAllowed = useSetTenantAppAllowed(tenantId);
  const pendingWriteAppIds = usePendingRowIds(
    appKeys.tenantAppWriteMutations(tenantId),
    (variables) => (variables as { appId: string }).appId
  );
  const removingAppIds = usePendingRowIds(appKeys.tenantAppLockMutations(tenantId));

  const catalog = useMemo(
    () => mergeAppCatalog(registryApps, installedApps, locale),
    [registryApps, installedApps, locale]
  );

  const addedApps = useMemo(() => catalog.filter((app) => app.installed), [catalog]);
  const candidates = useMemo(() => catalog.filter((app) => !app.installed), [catalog]);

  /**
   * Removing `$pull`s the whole installed-app subdoc, so it discards the app's
   * seat assignments AND its per-tenant config; an app holding either asks
   * first. An org-manager-only app is typically unseated but still configured,
   * so config alone must trigger the prompt — otherwise a configured app is
   * removed on one click and re-adding restores nothing. An app with neither
   * loses nothing and stays a single click.
   */
  const [removeCandidate, setRemoveCandidate] = useState<MergedAppRow | null>(null);

  const handleRemove = (app: MergedAppRow) => {
    if (hasSeatsOrConfig(app)) {
      setRemoveCandidate(app);
    } else {
      removeMutation.mutate(app.appId);
    }
  };

  const confirmRemove = () => {
    if (!removeCandidate) return;
    const { appId } = removeCandidate;
    // Not `mutate`'s per-call `onSuccess`: the observer runs it only for its
    // latest call, which a single-click Remove of another app replaces.
    // The hook's own `onError` reports a failure.
    removeMutation.mutateAsync(appId).then(
      () => setRemoveCandidate((current) => (current?.appId === appId ? null : current)),
      () => {}
    );
  };

  const renderHeaderAction = (app: MergedAppRow) => (
    <div className="flex shrink-0 items-center gap-3">
      {/* The switch reads as permission, so the tenant's own enabled/disabled
          state needs its own badge to stay visible up here. */}
      <AppStatusBadge status={appConsoleStatus(app)} />
      <AppDetailSwitch
        appId={app.appId}
        label={t('adminSettings.tenant.appAllowed')}
        checked={app.allowed}
        disabled={pendingWriteAppIds.has(app.appId)}
        onCheckedChange={() => setAllowed.mutate({ appId: app.appId, allowed: !app.allowed })}
      />
    </div>
  );

  // Each configurable app gets its own cell, typed to that app's config
  // shape — a third configurable app adds its own branch here.
  const renderConfigCell = (app: MergedAppRow) => {
    if (app.appId === PROCESS_ANALYZER_APP_ID) {
      return <TenantProcessAnalyzerConfigCell tenantId={tenantId} app={app} />;
    }
    if (app.appId === COMPLIANCE_CHECK_APP_ID) {
      return <TenantComplianceCheckConfigCell tenantId={tenantId} app={app} />;
    }
    // Not a config: the sales app reads its scope off the tenant (SAL-09), so
    // the cell only shows what the app will read here.
    if (app.appId === SALES_APP_ID && tenant) {
      return <TenantSalesScopeCell tenant={tenant} />;
    }
    return null;
  };

  // A partner tenant has no seats: every partner member runs the apps the
  // tenant holds (SAL-09), so a seat cap would promise a limit nothing enforces.
  const hasSeats = tenant?.tenantType !== 'partner';

  const renderDetail = (app: MergedAppRow) => {
    const configCell = renderConfigCell(app);

    return (
      <div className="space-y-6">
        {hasSeats && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">{t('adminSettings.tenant.appSeats')}</h3>
            <TenantAppSeatLimitCell tenantId={tenantId} app={app} />
          </div>
        )}

        {configCell && (
          <>
            {hasSeats && <Separator />}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">{t('adminSettings.tenant.appConfig')}</h3>
              {configCell}
            </div>
          </>
        )}

        <Separator />

        <div className="flex justify-end">
          <Button
            variant="destructive"
            className="shrink-0"
            loading={removingAppIds.has(app.appId)}
            onClick={() => handleRemove(app)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('adminSettings.tenant.removeApp')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">{t('adminSettings.tenant.tabApps')}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t('adminSettings.tenant.appsDescription')}
        </p>
      </div>

      <AppMasterDetail
        rows={addedApps}
        emptyMessage={t('adminSettings.tenant.noAppsAdded')}
        isLoading={registryLoading || appsLoading}
        railAction={
          <AddTenantAppDialog
            candidates={candidates}
            pendingAppId={addMutation.isPending ? (addMutation.variables ?? null) : null}
            onAdd={(appId, onAdded) => addMutation.mutateAsync(appId).then(onAdded, () => {})}
          />
        }
        renderHeaderAction={renderHeaderAction}
        renderDetail={renderDetail}
      />

      <ConfirmDialog
        open={!!removeCandidate}
        onOpenChange={(open) => {
          if (!open) setRemoveCandidate(null);
        }}
        title={t('adminSettings.tenant.removeAppTitle')}
        description={t('adminSettings.tenant.removeAppConfirm')}
        confirmLabel={t('common.confirm')}
        variant="danger"
        isPending={!!removeCandidate && removingAppIds.has(removeCandidate.appId)}
        onConfirm={confirmRemove}
      />
    </div>
  );
}
